import { USDT_ADDRESS } from '@/config/card'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { useQuery } from '@tanstack/react-query'
import { Address, decodeFunctionData, erc20Abi } from 'viem'
import { useChainId } from 'wagmi'
import { delegationTokenAbi } from '../../../../abis/evm/delegationToken'
import { CREDIT_TYPES } from '../constants'
import { createSmartFetch } from './useAuthenticatedMutation'
import { useCardAuth } from './useCardAuth'

interface UseCreditReceiverParams {
  creditType?: string
  userParams?: Record<string, unknown>
  enabled?: boolean
}

/**
 * Shared hook to fetch the credit receiver (spender/delegatee/operator) address from the backend.
 *
 * This ensures all approval/delegation hooks use the same address that the backend
 * uses for transactions, preventing mismatches between frontend checks and backend transactions.
 *
 * Uses the `hypurrFi` (borrow) solver to get a delegation transaction, then decodes the
 * `approveDelegation(delegatee, amount)` callData to extract the credit receiver address.
 * The `hypurrFi-withdraw` solver is not in the backend's IsSupportedForEndUser() allowlist.
 *
 * The address is cached for 5 minutes as it rarely changes.
 */
export function useCreditReceiver({
  creditType = CREDIT_TYPES.HYPURRFI_BORROW,
  userParams,
  enabled: externalEnabled = true,
}: UseCreditReceiverParams = {}) {
  const chainId = useChainId()
  // Use hasTokens (valid access token or resumable server session) instead of tokens (only valid access token).
  // This allows createSmartFetch to refresh through the HttpOnly cookie when needed.
  const { hasTokens } = useCardAuth()

  const {
    data: creditReceiver,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['credit-receiver', chainId, creditType, JSON.stringify(userParams ?? {})],
    queryFn: async () => {
      const decodeReceiver = (callData: string): Address => {
        const callDataHex = callData as `0x${string}`

        try {
          const decoded = decodeFunctionData({
            abi: delegationTokenAbi,
            data: callDataHex,
          })
          if (decoded.functionName === 'approveDelegation' && decoded.args) {
            return decoded.args[0] as Address
          }
        } catch {}

        const decoded = decodeFunctionData({
          abi: erc20Abi,
          data: callDataHex,
        })
        if (decoded.functionName === 'approve' && decoded.args) {
          return decoded.args[0] as Address
        }

        throw new Error('Failed to decode credit receiver address from API response')
      }

      const delegateFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
        method: 'POST',
        body: JSON.stringify({
          creditType,
          creditLimit: 1,
          chainID: chainId,
          userParams: {
            borrowTokenAddress: USDT_ADDRESS,
            ...(userParams ?? {}),
          },
        }),
      })

      try {
        const apiData = await delegateFetch({})
        const callData = apiData.data?.callData as string | undefined
        if (callData) {
          return decodeReceiver(callData)
        }
      } catch {}

      const fallbackFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
        method: 'POST',
        body: JSON.stringify({
          creditType: CREDIT_TYPES.HYPURRFI_BORROW,
          creditLimit: 1,
          chainID: chainId,
          userParams: {
            borrowTokenAddress: USDT_ADDRESS,
          },
        }),
      })

      const fallbackData = await fallbackFetch({})
      const fallbackCallData = fallbackData.data?.callData as string | undefined
      if (!fallbackCallData) {
        throw new Error('No callData returned from credit delegate API')
      }

      return decodeReceiver(fallbackCallData)
    },
    enabled: hasTokens && !!chainId && externalEnabled,
    staleTime: 60_000 * 30, // Cache for 30 minutes - credit receiver rarely changes
    gcTime: 60_000 * 60, // Keep in cache for 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  })

  return {
    creditReceiver: creditReceiver as Address | undefined,
    isLoading,
    isLoaded: isSuccess && !isLoading,
    isError,
    error,
    refetch,
  }
}
